import { useMemo } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { IVehicle } from "../../../models/Vehicle";
import { ILead } from "../../../models/Lead";
import ClickToCopy from "../../shared/ClickToCopy";
import useStockNumber from "../../../hooks/useStockNumber";
import { ebayEngineSearchUrl, ebayMakeModelYearCategoryQuery } from "../../../utils/ebaySearchString";
import EbayLinkButton from "../../shared/Inputs/EbayLinkButton";

export default function TableVehicleDetails({
  vehicle,
  price,
  lead = null,
  showEbaySearch = false,
}: {
  vehicle: IVehicle | null;
  price?: number;
  lead?: ILead | null
  showEbaySearch?: boolean;
}) {
  const { getStockNumber } = useStockNumber();

  const ebaySearchQuery = useMemo(
    () =>
      vehicle
        ? ebayMakeModelYearCategoryQuery({
          dvlaYearOfManufacture: vehicle.dvlaYearOfManufacture,
          combinedMake: vehicle.combinedMake,
          modelRangeDescription: vehicle.modelRangeDescription,
        })
        : "",
    [vehicle]
  );

  const canSearchEbay =
    showEbaySearch &&
    !!vehicle?.combinedMake &&
    !!vehicle?.modelRangeDescription;

  const ebayEngineUrl = useMemo(
    () => (vehicle ? ebayEngineSearchUrl(vehicle) : null),
    [vehicle]
  );

  const canSearchEngineEbay = showEbaySearch && !!ebayEngineUrl;

  const imageSrc = vehicle?.onSitePhotoThumbnail
    ? `data:image/png;base64, ${vehicle.onSitePhotoThumbnail}`
    : vehicle?.vehicleImageUrl;

  if (!vehicle) {
    return <span className="text-gray-500 italic">No Vehicle Data</span>;
  }

  return (
    <div className="flex gap-x-1 items-center relative">
      {/* price pill */}
      {price !== undefined && price !== null && price > 0 && (
        <span className="absolute top-0 left-0 bg-gray-200 border border-gray-400 text-gray-800 text-xs font-bold px-2 py-1 rounded-full">
          £{price?.toLocaleString() ?? "N/A"}
        </span>
      )}

      <span className="flex justify-center relative">
        {imageSrc ? (
          <img
            className="w-32 h-20 object-contain"
            src={imageSrc}
            alt={vehicle.vrm ?? "Vehicle"}
          />
        ) : (
          <CameraIcon className="w-14 mx-10 text-gray-600" />
        )}
      </span>

      <div className="flex flex-col">
        <ClickToCopy title="VRM" text={vehicle.vrm} />
        {vehicle.stockNumber && (
          <ClickToCopy title="Stock Number" text={getStockNumber(vehicle) ?? ""} textClassName="text-xs font-semibold" />
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="Make">{vehicle.combinedMake ?? ""}</span>
          {canSearchEbay && (
            <span onClick={(e) => e.stopPropagation()}>
              <EbayLinkButton query={ebaySearchQuery} />
            </span>
          )}
        </div>
        <span title="Model">{vehicle.modelRangeDescription ?? ""}</span>
        <ClickToCopy title="VIN" text={vehicle.combinedVIN} textClassName="text-xs" />
        <ClickToCopy
          title="Engine Model Code"
          text={vehicle.engineModelCode}
          textClassName="text-xs"
          className="w-full"
          middle={
            canSearchEngineEbay ? (
              <span onClick={(e) => e.stopPropagation()}>
                <EbayLinkButton
                  href={ebayEngineUrl!}
                  title="Search eBay for engine"
                />
              </span>
            ) : undefined
          }
        />
        <span title="Transmission and Fuel Type" className="text-xs">{vehicle.transmission ?? ""} {vehicle.fuelType ?? ""}</span>
      </div>

    </div>
  )
}